import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  contentDashboard,
  curatorDashboard,
  type ContentDashboard,
  type CuratorDashboard,
  type TraineeProgress,
} from "../api/analytics";
import { ApiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { AdmitDialog } from "../components/AdmitDialog";
import { formatMoscowDate } from "../utils/datetime";

type Tab = "curator" | "content";

const BUCKET_LABELS: Record<string, string> = {
  "0-25": "0–25%",
  "25-50": "25–50%",
  "50-75": "50–75%",
  "75-99": "75–99%",
  done: "Готово",
};

function rolesAllow(roles: string[], allowed: string[]): boolean {
  return roles.some((r) => allowed.includes(r));
}

export function AnalyticsPage() {
  const { user } = useCurrentUser();
  const canSeeCurator = user
    ? rolesAllow(user.roles, ["super_admin", "admin", "curator", "mentor"])
    : false;
  const canSeeContent = user
    ? rolesAllow(user.roles, ["super_admin", "admin", "content_manager"])
    : false;

  const [tab, setTab] = useState<Tab>(canSeeCurator ? "curator" : "content");
  const [curator, setCurator] = useState<CuratorDashboard | null>(null);
  const [content, setContent] = useState<ContentDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Admit dialog
  const [admitTarget, setAdmitTarget] = useState<TraineeProgress | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === "curator" && canSeeCurator) {
        setCurator(await curatorDashboard());
      } else if (tab === "content" && canSeeContent) {
        setContent(await contentDashboard());
      }
    } catch (err) {
      setError(err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна");
    } finally {
      setLoading(false);
    }
  }, [tab, canSeeCurator, canSeeContent]);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (!user) return null;
  if (!canSeeCurator && !canSeeContent) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 text-center text-slate-500">
        Нет доступа к аналитике.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-slate-900">Аналитика</h1>
        <Link
          to="/"
          className="btn-secondary"
        >
          ← На главную
        </Link>
      </header>

      {/* Tabs (shown only if both available) */}
      {canSeeCurator && canSeeContent && (
        <div className="mb-4 flex gap-2 border-b border-slate-200">
          <button
            type="button"
            onClick={() => setTab("curator")}
            className={`border-b-2 px-3 py-2 text-sm font-medium ${
              tab === "curator"
                ? "border-brand-accent text-brand-accent"
                : "border-transparent text-slate-600 hover:text-slate-900"
            }`}
          >
            Куратор
          </button>
          <button
            type="button"
            onClick={() => setTab("content")}
            className={`border-b-2 px-3 py-2 text-sm font-medium ${
              tab === "content"
                ? "border-brand-accent text-brand-accent"
                : "border-transparent text-slate-600 hover:text-slate-900"
            }`}
          >
            Контент
          </button>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center text-slate-400">Загрузка…</div>
      ) : tab === "curator" && curator ? (
        <CuratorView dashboard={curator} onAdmit={setAdmitTarget} />
      ) : tab === "content" && content ? (
        <ContentView dashboard={content} />
      ) : null}

      {admitTarget && admitTarget.course_id !== null && (
        <AdmitDialog
          userId={admitTarget.user_id}
          userName={admitTarget.full_name}
          courseId={admitTarget.course_id}
          onClose={() => setAdmitTarget(null)}
          onPromoted={() => {
            setAdmitTarget(null);
            void reload();
          }}
        />
      )}
    </div>
  );
}

function CuratorView({
  dashboard,
  onAdmit,
}: {
  dashboard: CuratorDashboard;
  onAdmit: (t: TraineeProgress) => void;
}) {
  return (
    <div className="space-y-6">
      {/* KPI tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="Учеников" value={dashboard.total_trainees} />
        <Kpi label="Средний прогресс" value={`${dashboard.avg_percent}%`} />
        <Kpi label="В риске" value={dashboard.at_risk_count} variant="warn" />
        <Kpi label="Активны (30д)" value={dashboard.completed_30d} />
      </div>

      {/* Buckets */}
      <section className="rounded-lg bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-base font-semibold text-slate-900">
          Распределение по этапам
        </h2>
        <div className="space-y-2">
          {Object.entries(BUCKET_LABELS).map(([key, label]) => {
            const v = dashboard.progress_buckets[key] ?? 0;
            const total = Math.max(
              1,
              Object.values(dashboard.progress_buckets).reduce((a, b) => a + b, 0),
            );
            return (
              <div key={key} className="flex items-center gap-3 text-sm">
                <div className="w-20 text-slate-600">{label}</div>
                <div className="flex-1 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-3 ${
                      key === "done" ? "bg-emerald-500" : "bg-brand-accent"
                    }`}
                    style={{ width: `${(v / total) * 100}%` }}
                  />
                </div>
                <div className="w-12 text-right text-slate-600">{v}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Ready for exam */}
      {dashboard.ready_for_exam.length > 0 && (
        <section>
          <h2 className="mb-3 text-base font-semibold text-slate-900">
            Готовы к допуску ({dashboard.ready_for_exam.length})
          </h2>
          <ul className="space-y-2">
            {dashboard.ready_for_exam.map((t) => (
              <li
                key={`${t.user_id}-${t.course_id}`}
                className="flex items-center justify-between rounded-lg bg-white p-3 shadow-sm"
              >
                <div>
                  <div className="font-medium text-slate-900">{t.full_name}</div>
                  <div className="text-xs text-slate-500">{t.course_title}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onAdmit(t)}
                  className="rounded bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700"
                >
                  Допустить
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* At risk */}
      {dashboard.at_risk.length > 0 && (
        <section>
          <h2 className="mb-3 text-base font-semibold text-slate-900">
            В риске — без активности 5+ дней ({dashboard.at_risk.length})
          </h2>
          <ul className="space-y-2">
            {dashboard.at_risk.map((t) => (
              <li
                key={`${t.user_id}-${t.course_id}`}
                className="rounded-lg bg-white p-3 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-slate-900">{t.full_name}</div>
                    <div className="text-xs text-slate-500">
                      {t.course_title} ·{" "}
                      {t.last_activity_at
                        ? `последний урок: ${formatMoscowDate(t.last_activity_at)}`
                        : "ни одного урока не пройдено"}
                    </div>
                  </div>
                  <div className="text-sm text-slate-700">{t.percent}%</div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function ContentView({ dashboard }: { dashboard: ContentDashboard }) {
  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-lg bg-white shadow-sm">
        <h2 className="border-b border-slate-100 px-4 py-3 text-base font-semibold text-slate-900">
          Курсы
        </h2>
        {dashboard.courses.length === 0 ? (
          <p className="p-6 text-center text-sm text-slate-500">Курсов нет.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Курс</th>
                <th className="px-4 py-2 text-right">Назначен</th>
                <th className="px-4 py-2 text-right">Закончили</th>
                <th className="px-4 py-2 text-right">Средн. %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {dashboard.courses.map((c) => (
                <tr key={c.course_id}>
                  <td className="px-4 py-2">
                    <Link
                      to={`/courses/${c.course_id}`}
                      className="text-slate-900 hover:text-brand-accent"
                    >
                      {c.course_title}
                    </Link>
                    <span className="ml-2 text-xs text-slate-400">{c.status}</span>
                  </td>
                  <td className="px-4 py-2 text-right text-slate-700">{c.assigned_count}</td>
                  <td className="px-4 py-2 text-right text-slate-700">{c.completed_count}</td>
                  <td className="px-4 py-2 text-right text-slate-700">{c.avg_percent}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-base font-semibold text-slate-900">
          Где застревают ученики (топ-{dashboard.stuck_lessons.length})
        </h2>
        {dashboard.stuck_lessons.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
            Нет данных — никто пока не открывал уроки.
          </p>
        ) : (
          <ul className="space-y-2">
            {dashboard.stuck_lessons.map((l) => (
              <li
                key={l.lesson_id}
                className="flex items-center justify-between rounded-lg bg-white p-3 shadow-sm"
              >
                <div>
                  <Link
                    to={`/lessons/${l.lesson_id}`}
                    className="font-medium text-slate-900 hover:text-brand-accent"
                  >
                    {l.title}
                  </Link>
                  <div className="text-xs text-slate-500">
                    Открыли {l.started_count}, прошли {l.completed_count}
                  </div>
                </div>
                <div className="text-sm font-medium text-red-700">
                  {l.drop_off_percent}% drop-off
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Kpi({
  label,
  value,
  variant,
}: {
  label: string;
  value: string | number;
  variant?: "warn";
}) {
  return (
    <div
      className={`rounded-lg p-3 shadow-sm ${
        variant === "warn" && Number(value) > 0
          ? "bg-amber-50"
          : "bg-white"
      }`}
    >
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
    </div>
  );
}
